/**
 * Skill definitions for the core content pack.
 *
 * Defines basic skills available in the base game.
 */

import type { SkillDefinition } from '@idle-depths/shared';

/**
 * Mining skill definition.
 *
 * The first gathering skill players will use.
 * Extracts ore, gems, and focus crystals from rocks.
 */
export const MINING_SKILL: SkillDefinition = {
  id: 'mining',
  name: 'Mining',
  description: 'Extract ore, gems, and focus crystals from rocks throughout the world.',
  category: 'gathering',
  icon: 'pickaxe',
  unlockedByDefault: true,
};

/**
 * All core skill definitions.
 *
 * Currently only includes Mining as the reference implementation.
 * Additional skills will be added as they are implemented.
 */
export const CORE_SKILLS: SkillDefinition[] = [MINING_SKILL];

/**
 * Type guard for SkillDefinition.
 *
 * Used by ContentRegistry for validation.
 *
 * @param data - Unknown data to validate
 * @returns True if data is a valid SkillDefinition
 */
export function isSkillDefinition(data: unknown): data is SkillDefinition {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const obj = data as Record<string, unknown>;

  return (
    typeof obj.id === 'string' &&
    typeof obj.name === 'string' &&
    typeof obj.description === 'string' &&
    typeof obj.category === 'string' &&
    ['gathering', 'artisan', 'combat', 'support'].includes(obj.category as string) &&
    typeof obj.icon === 'string' &&
    typeof obj.unlockedByDefault === 'boolean'
  );
}
